import { inject as service } from '@ember/service';
import { htmlSafe } from '@ember/template';

import { getStickersDeferred } from 'mewe/utils/emoji-utils';
import { reposition } from 'mewe/utils/popup-utils';

import PurchasesStore from 'mewe/stores/purchases-store';
import Scrolling from 'mewe/utils/scrolling-utils';
import dispatcher from 'mewe/dispatcher';

import StickerApi from 'mewe/api/sticker-api';
import { isEmpty } from '@ember/utils';

import PopupOpener from 'mewe/pods/components/basic-ui/mw-popup/base/component';
import { tracked } from '@glimmer/tracking';
import { A } from '@ember/array';
import { action, computed } from '@ember/object';
import { isPresent } from '@ember/utils';
import { dropTask } from 'ember-concurrency';
import layout from './template.hbs';
import './styles.scss';

const packUrls = {
  'spring-fling': 'assets/images/icons/stickers/logo/spring-fling-logo.png',
  'winter-joy': 'assets/images/icons/stickers/logo/winter-joy-logo.png',
  'donuts-jelly': 'assets/images/icons/stickers/logo/donuts-jelly.png',
  'avocado-adventures': 'assets/images/icons/stickers/logo/avocado-adventures.png',
  'eggplant-life': 'assets/images/icons/stickers/logo/eggplant-life.png',
  'happy-poo': 'assets/images/icons/stickers/logo/happy-poo.png',
  'mermaid-life': 'assets/images/icons/stickers/logo/mermaid-life.png',
  'peach-life': 'assets/images/icons/stickers/logo/peach-life.png',
  'smiley-guy': 'assets/images/icons/stickers/logo/smiley-guy.png',
  'unicorn-life': 'assets/images/icons/stickers/logo/unicorn-life.png',
  'woman-power': 'assets/images/icons/stickers/logo/woman-power.png',
  'sweet-sassy': 'assets/images/icons/stickers/logo/sweet-sassy.png',
  'halloween-party': 'assets/images/icons/stickers/logo/halloween-party-logo.png',
  'christmas-merry': 'assets/images/icons/stickers/christmas-merry.png',
  'naughty-dozen': 'assets/images/icons/stickers/naughty-dozen.png',
};

export default class MwStickersPicker extends PopupOpener {
  layout = layout;

  scrolling = Scrolling();
  popupType = 'stickers-picker';
  dropdownId = 'stickers-picker';

  closeOnScroll = false;
  closeOnHoverOutside = false;
  closeOnClickOutside = true;

  @service account;
  @service dynamicPopups;
  @service features;

  @tracked showPopup = true;
  @tracked stickers = null;
  @tracked selectedId = 'holiday23_free';
  @tracked aiStickers = A([]);
  @tracked freePacks = A(['stickers-holiday23_free', 'stickers-summer-fun_free']);
  @tracked scrollIndex = 0;
  @tracked visiblePacksCount = 8;
  @tracked element;
  @tracked showStickerPicker = true;
  @tracked
  packs = A([
    'holiday23_free',
    'summer-fun_free',
    'christmas-merry',
    'naughty-dozen',
    'spring-fling',
    'winter-joy',
    'donuts-jelly',
    'smiley-guy',
    'happy-poo',
    'avocado-adventures',
    'peach-life',
    'unicorn-life',
    'woman-power',
    'eggplant-life',
    'mermaid-life',
    'sweet-sassy',
    'halloween-party',
  ]);

  constructor() {
    super(...arguments);

    getStickersDeferred().promise.then((data) => {
      this.stickers = data;
    });

    this.storeState = PurchasesStore.getState();

    if (this.showMyAndAiStickers) {
      this.packs.unshift('my_sticker');
      this.packs.unshift('gen_sticker');
    }
    if (this.storeState.lastUsedStickerPack) {
      this.selectedId = this.storeState.lastUsedStickerPack;
      this.calculateScrollIndex();
    }
    if (isPresent(this.storeState.lastPurchasedItem)) {
      this.selectedId = this.storeState.lastPurchasedItem.replace('stickers-', '');
      this.calculateScrollIndex();
    }
  }

  calculateScrollIndex() {
    const isRtl = document.dir;
    let newIndex;

    if (this.packs.indexOf(this.selectedId) > this.packs.length - this.visiblePacksCount) {
      newIndex = -this.packs.indexOf(this.selectedId) + this.visiblePacksCount - 1;
    } else {
      newIndex = -this.packs.indexOf(this.selectedId);
    }

    this.scrollIndex = isRtl ? Math.abs(newIndex) : newIndex;
  }

  @action
  onInsertElement(element) {
    this.element = element;
    this.args.params.popupDidOpen?.();
  }

  @action
  onDestroy() {
    this.scrolling.unbindScrollDown(this.getDialogWrapper());
    this.args.params.popupDidClose?.();
    this.dynamicPopups.closeByPopupName('mw-stickers-picker');
  }

  get showMyAndAiStickers() {
    return this.features.isEnabled('web_are_ai_stickers_enabled') && this.args.params.aiStickerEnabled;
  }

  @computed('storeState.purchasedItems.length')
  get purchased() {
    return this.storeState.purchasedItems.filter((el) => ~el.indexOf('stickers-')).concat(this.freePacks);
  }

  get scrollStyle() {
    return htmlSafe(`transform: translateX(${this.scrollIndex * 48}px)`);
  }

  get selectedStickers() {
    return this.stickers && this.stickers[`stickers-${this.selectedId}`]?.stickers || [];
  }

  @computed('purchased','selectedId')
  get isOwned() {
    return this.purchased.indexOf('stickers-' + this.selectedId) > -1;
  }

  get selectedPackName() {
    return this.selectedId
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  get selectedPackLogoUrl() {
    return `/${packUrls[this.selectedId]}`;
  }

  get sprite() {
    if (!this.stickers) return null;

    return {
      url: this.stickers[`stickers-${this.selectedId}`].pngSpriteSheet.default,
      size: this.stickers[`stickers-${this.selectedId}`].pngSpriteSize,
    };
  }

  get arrowLeftVisible() {
    if (this.packs.length <= this.visiblePacksCount) return false;

    const isRtl = document.dir;
    return isRtl ? this.packs.length - this.scrollIndex > this.visiblePacksCount : this.scrollIndex < 0;
  }

  get arrowRightVisible() {
    if (this.packs.length <= this.visiblePacksCount) return false;

    const isRtl = document.dir;
    return isRtl ? this.scrollIndex > 0 : this.scrollIndex + this.packs.length > this.visiblePacksCount;
  }

  get isGeneratedStickers() {
    return this.selectedId === 'my_sticker' || this.selectedId === 'gen_sticker';
  }

  get isMyStickers() {
    return this.selectedId === 'my_sticker';
  }

  get generatedStickers() {
    if (isEmpty(this.aiStickers)) {
      return;
    }
    return this.aiStickers.map((sticker) => {
      sticker.url = `${location.origin}/api/v2/stickers/generated/${sticker.id}/file`;
      return sticker;
    });
  }

  get popupSize() {
    return {
      width: 392,
      height: 392,
    };
  }

  getPlacement() {
    const isRtl = document.dir;

    const placement = reposition(this.args.params.parent, this.popupSize),
      rectParent = this.args.params.parent.getBoundingClientRect(),
      rectParentLeft = rectParent.left + document.body.scrollLeft;

    //adjust placement for chat giphy
    if (this.args.params.origin === 'chat' || this.args.params.origin === 'expanded-chat') {
      this.element.style.position = 'fixed';

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

      placement.top = placement.top - scrollTop;
    }

    if (this.args.params.origin === 'chat') {
      if (!isRtl) placement.left = rectParentLeft - this.popupSize.width + 30;
    } else if (this.args.params.origin === 'expanded-chat') {
      if (isRtl) placement.left = rectParentLeft - this.popupSize.width + 30;
      else placement.left = rectParentLeft;
    }

    return placement;
  }

  getDialogWrapper() {
    return document.querySelector('.dialog_wrapper');
  }

  @action
  async loadMyStickers() {
    if (!this.features.isEnabled('web_are_ai_stickers_enabled')) {
      return;
    }

    this.loadGeneratedStickers.perform();
  }

  @dropTask * loadGeneratedStickers() {
    const response = yield StickerApi.loadGeneratedSticker();
    yield response.stickers.forEach(async (sticker) => {
      return await StickerApi.downloadGeneratedSticker(sticker.id);
    });
    this.aiStickers = response.stickers;
  }

  @action
  selectSticker(sticker) {
    if (sticker.status) {
      this.args.params.setSticker({ id: sticker.id, status: 'done' });
    } else {
      if (!this.isOwned) return;
      PurchasesStore.send('handleLastUsedStickerPack', this.selectedId);
      this.args.params.setSticker({ package: 'stickers-' + this.selectedId, id: sticker.name, data: sticker });
    }

    this.close();
  }

  @action
  selectPack(id, event) {
    event.stopPropagation();
    event.preventDefault();
    this.element.querySelector('main').scrollTop = 0;
    this.selectedId = id;
  }

  @action
  scrollLeft(event) {
    event.stopPropagation();
    event.preventDefault();
    this.scrollIndex--;
  }

  @action
  scrollRight(event) {
    event.stopPropagation();
    event.preventDefault();
    this.scrollIndex++;
  }

  @action
  buyPack() {
    this.dynamicPopups.closeAll();
    dispatcher.dispatch('purchase', 'checkout', 'stickers-' + this.selectedId);
  }
}
